import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {ComboBox, RadioGroupHandler, Text} from '@forecast-it/design-system';
import {ManualContactComboBox} from '../components/ManualContactComboBox';
import InvoiceProjectTables from '../components/invoice_project_tables';
import InvoiceDetailsForm from '../components/invoice_details_form';
import {
	GeneralSettingsContainer,
	InvoiceTypeSetting,
	InvoiceTypeOptions,
	InvoiceTypeOption,
	ClientDropdownContainer,
	ClientContactContainer,
} from '../../../../styles/v2/invoice_modals_v2_styled';
import {prepareProjectData} from '../../../../components/insights/invoicing/invoicing_data_prepare';
import {BUDGET_TYPE} from '../../../../constants';

const INVOICE_TYPE_BILLABLE = 'billable';
const INVOICE_TYPE_MANUAL = 'manual';

const ProjectSelectionOrInvoiceSettings = ({
	clients,
	startDate,
	setStartDate,
	endDate,
	setEndDate,
	company,
	selectedProjectsIds,
	selectedClientId,
	isClientChangeable = true,
	isCreatingBlankInvoice,
	setSelectedProjectsIds,
	setSelectedClientId,
	setIsCreatingBlankInvoice,
	setInvoice,
	invoice,
	currenciesDropdownOptions,
	isEditing,
	clientProjects,
	setHasError,
	projectId,
	modalRefetch,
	isProjectInvoicing,
	project,
	contactsFeatureEnabled,
	getContacts,
	getSelectedContact,
	setSelectedContact,
}) => {
	const [clientOptions, setClientOptions] = useState([]);
	const intl = useIntl();
	const billableLabel = intl.formatMessage({id: 'invoicing.invoice_type_option.billable_fixed_price'});
	const manualLabel = intl.formatMessage({id: 'invoicing.invoice_type_option.manual'});

	// Manual/deposit only ever has one concrete client (no "All clients" option), so a single
	// Contact field scoped to selectedClientId is unambiguous here - unlike the billable flow,
	// where contact selection lives per project row instead. Always shown alongside Client (not
	// gated on a client being picked yet) so Client never moves/hides Contact as the user picks a
	// client - it just renders empty until manualContactClientId resolves to a real client.
	const showManualContactField = contactsFeatureEnabled && isCreatingBlankInvoice;
	// selectedClientId is the -1 ("All Clients") sentinel until a real client is picked - guard
	// against treating that placeholder (or a null/undefined selection) as a genuine client id when
	// querying/storing contacts, so the field reads as genuinely empty rather than looking up a
	// bogus "-1" client.
	const manualContactClientId = selectedClientId && selectedClientId !== -1 ? String(selectedClientId) : null;

	const projectData = prepareProjectData(
		clientProjects,
		company,
		company.currency,
		startDate,
		endDate,
		null,
		isProjectInvoicing
	);

	const showTimeMaterialSection = !isProjectInvoicing || project?.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS;

	const handleProjectOptionClick = projectId => {
		if (selectedProjectsIds.includes(projectId)) {
			setSelectedProjectsIds(selectedProjectsIds.filter(id => id !== projectId));
		} else {
			setSelectedProjectsIds([...selectedProjectsIds, projectId]);
		}
	};

	const handleSelectAllClick = projectIds => {
		const allSelected = projectIds.every(projectId => selectedProjectsIds.includes(projectId));
		if (allSelected) {
			setSelectedProjectsIds(selectedProjectsIds.filter(id => !projectIds.includes(id)));
		} else {
			const unselectProjectIds = projectIds.filter(id => !selectedProjectsIds.includes(id));
			setSelectedProjectsIds([...selectedProjectsIds, ...unselectProjectIds]);
		}
	};

	useEffect(() => {
		if (!isCreatingBlankInvoice) {
			const hasError = !selectedClientId || !selectedProjectsIds.length;
			setHasError(hasError);
		}
	}, [isCreatingBlankInvoice, selectedProjectsIds, selectedClientId]);

	useEffect(() => {
		let clientsDropdownOptions = clients.edges
			.map(client => ({value: client.node.id, label: client.node.name}))
			.sort(function (a, b) {
				return a.label.localeCompare(b.label);
			});
		if (!isCreatingBlankInvoice) {
			clientsDropdownOptions.unshift({value: -1, label: intl.formatMessage({id: 'common.all_clients'})});
		}
		setClientOptions(clientsDropdownOptions);
	}, [isCreatingBlankInvoice]);

	const autoSelectsProjects = !projectId && !isCreatingBlankInvoice;
	const belongsToClient = project => project.client && project.client.id === selectedClientId;

	// Picking a client (and the client the modal opens with) ticks every project listed for it.
	// The parent only passes the selected client's projects, so this runs once the new client's
	// list has rendered rather than inside onClientChange.
	useEffect(() => {
		if (autoSelectsProjects) {
			const invoicableProjects = projectData.time_material.projects
				.concat(projectData.fixed_price.projects)
				.concat(projectData.retainer.projects);
			setSelectedProjectsIds(invoicableProjects.filter(belongsToClient).map(p => p.id));
		}
	}, [selectedClientId]);

	// Counts refetches caused by the user changing the dates. Each one re-ticks the T&M projects
	// listed for the new range, once that list has landed; other refetches leave ticks alone.
	const [dateRefetchCount, setDateRefetchCount] = useState(0);
	const isMountedRef = useRef(true);
	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[]
	);

	useEffect(() => {
		if (dateRefetchCount === 0 || !autoSelectsProjects) return;
		const visibleTimeMaterialIds = projectData.time_material.projects.filter(belongsToClient).map(p => p.id);
		const timeMaterialIds = new Set(
			(clientProjects || [])
				.filter(p => p.budgetType === BUDGET_TYPE.TIME_AND_MATERIALS)
				.map(p => p.id)
				.concat(visibleTimeMaterialIds)
		);
		setSelectedProjectsIds(selectedProjectsIds.filter(id => !timeMaterialIds.has(id)).concat(visibleTimeMaterialIds));
	}, [dateRefetchCount]);

	const onClientChange = clientId => {
		setSelectedProjectsIds([]); //remove all previously selected projects when changing the client
		setSelectedClientId(clientId);
	};

	const handleClientSelected = key => {
		if (!isClientChangeable) return;
		const option = clientOptions.find(clientOption => String(clientOption.value) === key);
		if (option && option.value !== selectedClientId) onClientChange(option.value);
	};

	const handleInvoiceTypeChange = invoiceType => {
		if (invoiceType === INVOICE_TYPE_MANUAL) {
			if (selectedClientId === -1) {
				setSelectedClientId(null);
			}
			setIsCreatingBlankInvoice(true);
		} else {
			if (!selectedClientId) {
				setSelectedClientId(-1);
			}
			setIsCreatingBlankInvoice(false);
		}
	};

	const handleDateRangeChange = (newStartDate, newEndDate) => {
		const onDateRefetched = error => {
			if (!error && isMountedRef.current) setDateRefetchCount(count => count + 1);
		};
		if (modalRefetch) {
			modalRefetch(
				{
					startYear: newStartDate.year(),
					startMonth: newStartDate.month() + 1,
					startDay: newStartDate.date(),
					endYear: newEndDate.year(),
					endMonth: newEndDate.month() + 1,
					endDay: newEndDate.date(),
				},
				null,
				onDateRefetched,
				{force: true}
			);
		} else {
			onDateRefetched();
		}
		setStartDate(newStartDate);
		setEndDate(newEndDate);
	};

	return (
		<>
			<GeneralSettingsContainer>
				<InvoiceTypeSetting>
					<RadioGroupHandler
						label={intl.formatMessage({id: 'invoicing.invoice_type_label'})}
						value={isCreatingBlankInvoice ? INVOICE_TYPE_MANUAL : INVOICE_TYPE_BILLABLE}
						onValueChange={handleInvoiceTypeChange}
					>
						<InvoiceTypeOptions>
							<InvoiceTypeOption>
								<RadioGroupHandler.Item value={INVOICE_TYPE_BILLABLE} aria-label={billableLabel} />
								<Text>{billableLabel}</Text>
							</InvoiceTypeOption>
							<InvoiceTypeOption>
								<RadioGroupHandler.Item value={INVOICE_TYPE_MANUAL} aria-label={manualLabel} />
								<Text>{manualLabel}</Text>
							</InvoiceTypeOption>
						</InvoiceTypeOptions>
					</RadioGroupHandler>
				</InvoiceTypeSetting>
				<ClientContactContainer>
					{!isProjectInvoicing && (
						<ClientDropdownContainer data-userpilot="client-dropdown">
							<ComboBox
								data-cy="invoice-client-combobox"
								label={intl.formatMessage({id: 'common.client'})}
								placeholder={intl.formatMessage({id: 'common.client'})}
								required={true}
								disabled={!isClientChangeable}
								defaultItems={clientOptions.map(option => ({id: String(option.value), label: option.label}))}
								value={
									selectedClientId === null || selectedClientId === undefined
										? null
										: String(selectedClientId)
								}
								onChange={handleClientSelected}
								menuTrigger="focus"
							>
								{item => (
									<ComboBox.Item textValue={item.label}>
										<Text>{item.label}</Text>
									</ComboBox.Item>
								)}
							</ComboBox>
						</ClientDropdownContainer>
					)}
					{showManualContactField && (
						<ClientDropdownContainer>
							<ManualContactComboBox
								contacts={getContacts(manualContactClientId).contacts}
								loading={getContacts(manualContactClientId).loading}
								value={getSelectedContact(manualContactClientId, manualContactClientId)}
								onChange={contact =>
									manualContactClientId && setSelectedContact(manualContactClientId, contact)
								}
								dataCy="invoice-contact-dropdown"
							/>
						</ClientDropdownContainer>
					)}
				</ClientContactContainer>
			</GeneralSettingsContainer>
			{isCreatingBlankInvoice ? (
				<InvoiceDetailsForm
					isEditing={isEditing}
					setInvoice={setInvoice}
					invoice={invoice}
					currenciesDropdownOptions={currenciesDropdownOptions}
					isCreatingBlankInvoice={true}
					currency={company.currency}
					selectedProjectsIds={selectedProjectsIds}
					setHasError={setHasError}
				/>
			) : clientProjects && clientProjects.length ? (
				<InvoiceProjectTables
					selectedProjectsIds={selectedProjectsIds}
					onProjectSelect={projectId => handleProjectOptionClick(projectId)}
					onSelectAll={projectIds => handleSelectAllClick(projectIds)}
					projectData={projectData}
					showTimeMaterialSection={showTimeMaterialSection}
					startDate={startDate}
					endDate={endDate}
					onDateRangeChange={handleDateRangeChange}
					isProjectInvoicing={isProjectInvoicing}
					contactsFeatureEnabled={contactsFeatureEnabled}
					getContacts={getContacts}
					getSelectedContact={getSelectedContact}
					setSelectedContact={setSelectedContact}
				/>
			) : null}
		</>
	);
};

export default ProjectSelectionOrInvoiceSettings;
